import { useState, useCallback } from 'react';
import { TransactionService } from '../services/TransactionService';
import { Transaction } from '../types/accounting';

// Define a local Transaction interface that matches what the hook expects
interface HookTransaction {
  id: string;
  fiscalSponsorId: string;
  causeId?: string;
  projectId?: string;
  amount: number;
  date: string; // This is string in the hook but Date in accounting.ts
  description?: string;
  category: string;
  status: string;
  metadata?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

// Define the filters interface
interface TransactionFilters {
  startDate?: Date;
  endDate?: Date;
  minAmount?: number;
  maxAmount?: number;
  status?: string;
  type?: string;
  // Add other filter properties as needed
}

export function useTransactionService() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const service = new TransactionService();

  const getTransactions = useCallback(async (filters: TransactionFilters = {}) => {
    setLoading(true);
    try {
      const response = await service.getTransactions(filters);
      return response.data;
    } catch (err) {
      setError(err as Error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getTransactionById = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const transaction = await service.getTransactions({ id });
      const foundTransaction = transaction.data?.find((t: Transaction) => t.id === id);
      return foundTransaction ? convertToHookTransaction(foundTransaction) : null;
    } catch (err) {
      setError(err as Error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createTransaction = useCallback(async (data: Partial<HookTransaction>): Promise<HookTransaction | null> => {
    setLoading(true);
    try {
      const serviceData = convertToServiceTransaction(data);
      const created = await service.createTransaction(serviceData);
      return created ? convertToHookTransaction(created) : null;
    } catch (err) {
      setError(err as Error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTransaction = useCallback(async (id: string, data: Partial<HookTransaction>): Promise<HookTransaction | null> => {
    setLoading(true);
    try {
      const serviceData = convertToServiceTransaction(data);
      const updated = await service.updateTransaction(id, serviceData);
      return updated ? convertToHookTransaction(updated) : null;
    } catch (err) {
      setError(err as Error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteTransaction = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    try {
      await service.deleteTransaction(id);
      return true;
    } catch (err) {
      setError(err as Error);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper function to convert between types
  const convertToHookTransaction = (transaction: Transaction): HookTransaction => {
    return {
      id: transaction.id,
      fiscalSponsorId: transaction.fiscalSponsorId,
      causeId: transaction.causeId,
      projectId: transaction.projectId,
      amount: transaction.amount,
      date: transaction.date instanceof Date 
        ? transaction.date.toISOString().split('T')[0] 
        : String(transaction.date),
      description: transaction.description,
      category: transaction.category,
      status: transaction.status,
      metadata: transaction.metadata,
      createdAt: transaction.createdAt instanceof Date 
        ? transaction.createdAt.toISOString() 
        : String(transaction.createdAt),
      updatedAt: transaction.updatedAt instanceof Date 
        ? transaction.updatedAt.toISOString() 
        : String(transaction.updatedAt)
    };
  };

  // Helper function to convert hook transaction to service transaction
  const convertToServiceTransaction = (transaction: Partial<HookTransaction>): Partial<Transaction> => {
    const result: Partial<Transaction> = { 
      ...Object.entries(transaction).reduce((acc, [key, value]) => {
        if (key !== 'date') {
          (acc as any)[key] = value;
        }
        return acc;
      }, {} as Partial<Transaction>)
    };
    
    // Convert string date to Date object if present
    if (transaction.date) {
      result.date = new Date(transaction.date);
    }
    
    return result;
  };

  return {
    loading,
    error,
    getTransactions,
    getTransactionById,
    createTransaction,
    updateTransaction,
    deleteTransaction
  };
} 